/**
 * Application-owned media route backed by Replit App Storage.
 *
 * The browser receives this route, never a provider URL or storage credential.
 */
export const APP_STORAGE_PATH_PREFIX = "/app-storage/";

/**
 * Storage namespaces that retain originals or intermediate work but must never
 * be fetched by a browser. Public application media uses every other key.
 */
export const PRIVATE_APP_STORAGE_PREFIXES = [
  "generated/",
  "private/community-spotlight-staging/",
  "speaker-portrait-sources/",
  "speaker-portrait-candidates/",
  "member-event-resources/",
  // Member downloads. Served by their own route, which checks who is asking.
  "event-documents/",
] as const;

/**
 * Extract an App Storage key from either the relative application route or an
 * absolute URL on the application's managed media origin.
 */
export function appStorageKeyFromUrl(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;

  let pathname = trimmed;
  try {
    pathname = new URL(trimmed).pathname;
  } catch {
    // Relative application paths remain path-shaped.
  }

  if (!pathname.startsWith(APP_STORAGE_PATH_PREFIX)) return null;
  return pathname.slice(APP_STORAGE_PATH_PREFIX.length).split(/[?#]/)[0] || null;
}

/**
 * Apply the same normalized private-prefix policy in server routes and browser
 * fallbacks. Keeping this shared prevents the frontend from rejecting valid
 * `/app-storage/speaker-portraits/...` URLs while the server allows them.
 */
/**
 * A storage key in the one form a prefix policy can be applied to.
 *
 * Percent-decoded until stable, backslashes read as slashes, lower-cased, and
 * dot segments resolved — so `%2E%2E/generated/x.png`, `GENERATED\\x.png` and
 * `./generated/x.png` all become `generated/x.png` before any prefix is tested.
 * A prefix test that can be walked around with encoding, separator or traversal
 * tricks is not a control. Anything that cannot be decoded is null: fail closed.
 *
 * Shared by every route that decides what a key may be read as, so the public
 * proxy and the member download route cannot disagree about what a key says.
 */
export function normalizeStorageKeyForPolicy(rawKey: unknown): string | null {
  if (typeof rawKey !== "string") return null;

  let key = rawKey.trim();
  if (!key) return null;

  for (let pass = 0; pass < 3; pass += 1) {
    let decoded: string;
    try {
      decoded = decodeURIComponent(key);
    } catch {
      return null;
    }
    if (decoded === key) break;
    key = decoded;
  }

  key = key.replace(/\\/g, "/").toLowerCase();

  const segments: string[] = [];
  for (const segment of key.split("/")) {
    if (!segment || segment === ".") continue;
    if (segment === "..") {
      segments.pop();
      continue;
    }
    segments.push(segment);
  }

  const normalized = segments.join("/");
  return normalized || null;
}

export function isPubliclyServableStorageKey(rawKey: string): boolean {
  const normalized = normalizeStorageKeyForPolicy(rawKey);
  if (!normalized) return false;

  if (PRIVATE_APP_STORAGE_PREFIXES.some(prefix => normalized.startsWith(prefix))) return false;

  /*
   * Responsive derivatives wrap the source key:
   * responsive/<source-key>/w-640.webp. Re-check the wrapped key so a private
   * source namespace cannot become public merely by adding `responsive/`.
   */
  if (normalized.startsWith("responsive/")) {
    const responsiveMatch = normalized.match(
      /^responsive\/(.+)\/(?:w-\d+\.webp|manifest\.json)$/i,
    );
    if (!responsiveMatch) return false;
    const wrapped = responsiveMatch[1];
    if (
      !wrapped
      || PRIVATE_APP_STORAGE_PREFIXES.some(prefix => wrapped.startsWith(prefix))
    ) {
      return false;
    }
  }

  return true;
}